import type {
  AuthApiPort,
  AuthStorePort,
  UserLookupPort,
} from '../ports'
import type {
  AuthResponse,
  UpdatePasswordInput,
  UpdateProfileInput,
  User,
} from '../domain/models'

function normalizeIdentity(identity: string) {
  return identity.trim().toLowerCase()
}

export async function loginWithPassword(
  identity: string,
  password: string,
  deps: {
    authStore: AuthStorePort
    authApi: AuthApiPort
  },
): Promise<AuthResponse> {
  const normalizedIdentity = normalizeIdentity(identity)
  const response = await deps.authApi.loginWithPassword(
    normalizedIdentity,
    password,
  )
  deps.authStore.save(response.token, response.user)
  return response
}

export async function logout(authStore: AuthStorePort) {
  authStore.clear()
}

/**
 * True when the server explicitly rejected the credentials or device
 * (401/403, e.g. expired token or revoked kiosk). Anything else — network
 * failure, timeout, 5xx — is transient: keep the session and retry, or a
 * Wi-Fi blip during a background refresh silently logs the device out.
 */
export function isAuthRejection(error: unknown): boolean {
  const status = (error as { status?: unknown })?.status
  return status === 401 || status === 403
}

export async function refreshAuth(deps: {
  authStore: AuthStorePort
  authApi: AuthApiPort
  deviceUuid?: string
}): Promise<AuthResponse> {
  const response = await deps.authApi.refreshAuth(deps.deviceUuid)
  deps.authStore.save(response.token, response.user)
  return response
}

export async function updateProfile(
  input: UpdateProfileInput,
  deps: {
    authStore: AuthStorePort
    authApi: AuthApiPort
  },
): Promise<User> {
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('No authenticated user available for profile update')
  }

  const updatedUser = await deps.authApi.updateProfile(
    currentUser.id,
    input,
  )
  const token = deps.authStore.token
  if (token) {
    deps.authStore.save(token, updatedUser)
  }
  return updatedUser
}

export async function updatePassword(
  input: UpdatePasswordInput,
  deps: {
    authStore: AuthStorePort
    authApi: AuthApiPort
  },
): Promise<void> {
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('No authenticated user available for password update')
  }

  await deps.authApi.updatePassword(currentUser.id, input)
}

export function getCurrentUser(authStore: AuthStorePort): User | null {
  return authStore.user
}

export function getAuthorDisplayName(
  authorId: string,
  deps: {
    authStore: AuthStorePort
    userLookup: UserLookupPort
    userDirectory: User[]
  },
): string {
  if (!authorId.trim()) return 'System'
  const currentUser = deps.authStore.user

  if (currentUser && currentUser.id === authorId) {
    const isKioskTabletUser = Boolean(currentUser.deviceUuid || currentUser.kioskId)
    return isKioskTabletUser ? 'Kiosk' : currentUser.name
  }

  const user = deps.userLookup.getById(authorId)
  if (user && user.name) {
    return user.name
  }

  const userDirectoryMatch = deps.userDirectory.find(
    (directoryUser) => directoryUser.id === authorId,
  )
  if (userDirectoryMatch) {
    return userDirectoryMatch.name
  }

  return 'Unknown user'
}
